import type {
  SharedCircleGoalResponse,
  SharedCircleGoalSlotResponse,
} from "@consistency-circle/shared";
import {
  CheckCircle2Icon,
  CircleDashedIcon,
  Clock3Icon,
  SkipForwardIcon,
  XCircleIcon,
} from "lucide-react";

import { Progress } from "@/components/ui/progress";

function statusCopy(status: SharedCircleGoalSlotResponse["status"]): string {
  if (status === "DONE") return "Done";
  if (status === "SKIPPED") return "Skipped";
  if (status === "MISSED") return "Missed";
  return "Open";
}

function statusClasses(status: SharedCircleGoalSlotResponse["status"]): string {
  if (status === "DONE") return "border-primary/25 bg-primary/10 text-primary";
  if (status === "SKIPPED") return "border-muted-foreground/20 bg-muted text-muted-foreground";
  if (status === "MISSED") return "border-destructive/20 bg-destructive/10 text-destructive";
  return "border-border bg-background text-muted-foreground";
}

function StatusIcon({ status }: { readonly status: SharedCircleGoalSlotResponse["status"] }) {
  if (status === "DONE") return <CheckCircle2Icon className="h-3.5 w-3.5" />;
  if (status === "SKIPPED") return <SkipForwardIcon className="h-3.5 w-3.5" />;
  if (status === "MISSED") return <XCircleIcon className="h-3.5 w-3.5" />;
  return <Clock3Icon className="h-3.5 w-3.5" />;
}

export function SharedGoalCard({
  goal,
  ownerName,
}: {
  readonly goal: SharedCircleGoalResponse;
  readonly ownerName: string;
}) {
  const sortedSlots = goal.slots.slice().sort((left, right) => left.sortOrder - right.sortOrder);
  const done = sortedSlots.filter((slot) => slot.status === "DONE").length;
  const skipped = sortedSlots.filter((slot) => slot.status === "SKIPPED").length;
  const missed = sortedSlots.filter((slot) => slot.status === "MISSED").length;
  const total = sortedSlots.length;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold">{goal.name}</p>
          <p className="text-xs text-muted-foreground">{ownerName}'s goal</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-lg font-semibold tabular-nums">{total === 0 ? "-" : `${percent}%`}</p>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">today</p>
        </div>
      </div>

      {total > 0 ? (
        <>
          <Progress value={percent} className="h-1.5" />
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-primary/10 px-2 py-1 font-medium text-primary">
              {done} done
            </span>
            {skipped > 0 && (
              <span className="rounded-full bg-muted px-2 py-1 font-medium text-muted-foreground">
                {skipped} skipped
              </span>
            )}
            {missed > 0 && (
              <span className="rounded-full bg-destructive/10 px-2 py-1 font-medium text-destructive">
                {missed} missed
              </span>
            )}
          </div>
          <div className="space-y-2">
            {sortedSlots.map((slot) => (
              <div
                key={slot.id}
                className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${statusClasses(slot.status)}`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <StatusIcon status={slot.status} />
                  <span className="truncate text-sm font-medium">{slot.name}</span>
                </div>
                <span className="shrink-0 text-xs tabular-nums">
                  {statusCopy(slot.status)} - {slot.dueTimeLocal}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground">
          <CircleDashedIcon className="h-4 w-4" />
          No active subtasks today
        </div>
      )}
    </div>
  );
}
